import type { VerticalConfig } from '@selenos/leads';
import {
  SPITEX_DEMAND_NURTURE_SEQUENCE,
  SPITEX_DEMAND_SEQUENCE,
  SPITEX_SUPPLY_SEQUENCE,
} from '@selenos/outreach/sequences/spitex';
import { z } from 'zod';

import {
  spitexDemandConversationConfig,
  spitexSupplyConversationConfig,
} from './conversation-config';
import {
  cantonLabels,
  medizinischeAusbildungLabels,
  serviceInterestLabels,
} from './labels';

/**
 * Swiss cantons for Spitex operations.
 */
export const SwissCantonSchema = z.enum([
  'AG',
  'AI',
  'AR',
  'BE',
  'BL',
  'BS',
  'FR',
  'GE',
  'GL',
  'GR',
  'JU',
  'LU',
  'NE',
  'NW',
  'OW',
  'SG',
  'SH',
  'SO',
  'SZ',
  'TG',
  'TI',
  'UR',
  'VD',
  'VS',
  'ZG',
  'ZH',
]);

export type SwissCanton = z.infer<typeof SwissCantonSchema>;

/**
 * Swiss nursing qualification levels.
 * Uses standard Swiss abbreviations recognized on job portals and by employers.
 */
export const SwissQualificationSchema = z.enum([
  'hf_pflege',
  'fh_pflege',
  'fage',
  'fabe',
  'ags',
  'pflegehilfe_srk',
  'physiotherapist',
  'occupational_therapist',
  'midwife',
  'no_qualification',
  'other',
]);

export type SwissQualification = z.infer<typeof SwissQualificationSchema>;

/**
 * Custom fields for Spitex supply-side leads (caregivers/nurses).
 *
 * Two paths via serviceInterest routing:
 * - Angehoerigenpflege: family caregivers (no qualifications needed)
 * - Professional: nurses/carers (qualifications matter)
 */
export const SpitexSupplyFieldsSchema = z.object({
  serviceInterest: z
    .enum(['angehoerigenpflege', 'betreuung', 'hauswirtschaft'])
    .optional(),
  careRelationship: z
    .string()
    .optional()
    .describe('Who does the person care for (Mutter, Vater, Partner)'),
  careActivities: z
    .string()
    .optional()
    .describe('What care activities they perform (Waschen, Ankleiden)'),
  careHoursPerWeek: z
    .number()
    .positive()
    .optional()
    .describe('Hours per week of caregiving'),
  krankenkasse: z
    .string()
    .min(1)
    .optional()
    .describe(
      "The patient's health insurer (Krankenkasse). Operator-required (Osman, 2026-06-06): a supply lead is only forwardable once this is collected. Optional at the Zod level (incremental capture writes it field-by-field); the completion gate in conversation-config enforces presence before mayComplete.",
    ),
  medizinischeAusbildung: z
    .enum(['ja_abgeschlossen', 'in_ausbildung', 'nein'])
    .optional()
    .describe(
      'Whether the caregiver has medical training. CAPTURED, never a disqualifier: Spitex Frei employs untrained caregivers and covers the mandatory Pflegehelferkurs post-hire. Optional everywhere.',
    ),
  city: z.string().optional().describe('City/town of residence'),
  qualifications: z
    .array(SwissQualificationSchema)
    .default([])
    .describe('Swiss nursing qualifications (HF, FH, FaGe, AGS, etc.)'),
  specializations: z
    .array(
      z.enum([
        'geriatric_care',
        'palliative_care',
        'dementia_care',
        'wound_care',
        'pediatric_home_care',
        'psychiatric_care',
        'rehabilitation',
        'diabetes_management',
        'post_surgical',
        'maternity',
      ]),
    )
    .default([]),
  languages: z
    .array(
      z.enum(['de', 'fr', 'it', 'en', 'pt', 'es', 'sq', 'sr', 'tr', 'other']),
    )
    .default([]),
  canton: SwissCantonSchema.optional(),
  availability: z
    .enum(['full_time', 'part_time', 'on_call', 'weekends_only', 'flexible'])
    .optional(),
  experienceYears: z.number().int().nonnegative().optional(),
  isGrenzgaenger: z
    .boolean()
    .optional()
    .describe('Cross-border worker (Grenzgaenger)?'),
  countryOfResidence: z
    .enum(['CH', 'DE', 'FR', 'IT', 'AT', 'other'])
    .optional()
    .describe('Country of residence for cross-border workers'),
  diplomaRecognized: z
    .boolean()
    .optional()
    .describe('Swiss diploma recognition (SRK) completed?'),
});

export type SpitexSupplyFields = z.infer<typeof SpitexSupplyFieldsSchema>;

/**
 * Referral source for demand-side leads.
 */
export const ReferralSourceSchema = z.enum([
  'google_search',
  'opan',
  'hospital_referral',
  'gp_referral',
  'municipal_referral',
  'word_of_mouth',
  'comparis',
  'other',
]);

export type ReferralSource = z.infer<typeof ReferralSourceSchema>;

export const SpitexDemandFieldsSchema = z.object({
  serviceType: z
    .enum([
      'behandlungspflege',
      'angehoerigenpflege',
      'betreuung',
      'hauswirtschaft',
    ])
    .optional()
    .describe(
      'Which service the patient needs (behandlungspflege is the primary hook)',
    ),
  familyAlreadyCaring: z
    .boolean()
    .optional()
    .describe('Is a family member already providing unpaid care?'),
  canton: SwissCantonSchema.optional(),
  urgency: z
    .enum(['sofort', 'innerhalb_1_woche', 'innerhalb_1_monat', 'spaeter'])
    .optional()
    .describe('How soon is care needed?'),
  careDescription: z
    .string()
    .max(500)
    .optional()
    .describe('Free-text description of care needs (no medical details)'),
  referralSource: ReferralSourceSchema.optional().describe(
    'How did the patient/family find us? (captured via UTM, not chatbot)',
  ),
});

export type SpitexDemandFields = z.infer<typeof SpitexDemandFieldsSchema>;

/**
 * Default vertical configuration for Spitex supply-side (caregiver recruitment).
 *
 * Targets Swiss market, DSG+health compliant, sourced via job boards and Meta Ads.
 * Outreach through email, WhatsApp, and SMS.
 */
export const spitexSupplyConfig: VerticalConfig = {
  id: 'a1000000-0000-0000-0000-000000000003',
  name: 'Spitex Caregivers',
  slug: 'spitex-supply',
  adCampaignPatterns: ['spitex_supply', 'spitex-supply'],
  country: 'CH',
  language: 'de',
  capture: {
    // The Selenos default primitive: native CTWA -> supply WABA session ->
    // caregiver agent qualifies in-chat (ADR 0002).
    primitive: 'whatsapp_agent',
  },

  customFieldsSchema: SpitexSupplyFieldsSchema,
  conversationConfig: spitexSupplyConversationConfig,

  /**
   * Map the raw Spitex_Supply Instant-Form answer STRINGS onto valid schema
   * values + scoreable fields. The Meta form sends the human LABELS keyed by
   * the form question name:
   *   - canton: "Zuerich" / "Aargau" / ... (the German label, NOT the "ZH" code)
   *   - serviceInterest: "Angehoerigenpflege (bezahlt)" / "Betreuung" / "Hauswirtschaft"
   *   - careRelationship: "Ja, regelmaessig" / "Ja, gelegentlich" / "Nein, noch nicht"
   *
   * Without this mapping, `canton`="Zuerich" and `serviceInterest`="…(bezahlt)"
   * are NOT valid `SwissCantonSchema` / serviceInterest-enum values, so the
   * capture `.strict()` gate REJECTS the lead (the #226 bounce class) — and even
   * if it ingested, scoring reads the enum code, never the label, so the lead
   * scores 0. We translate the labels back to the canonical enum values here and
   * keep `careRelationship` as-is (scored on its raw string). Runs BEFORE the
   * `.strict()` gate + scoring. Idempotent: an already-canonical value
   * (e.g. "ZH" / "angehoerigenpflege") is left untouched.
   */
  normalizeFormAnswers: (customFields: Record<string, unknown>) => {
    const out = { ...customFields };

    // Reverse label → enum maps, built once from the shared label sources so a
    // label edit can never drift from the form. Case-insensitive on the value.
    const cantonByLabel = new Map<string, string>(
      Object.entries(cantonLabels).map(([code, label]) => [
        label.toLowerCase(),
        code,
      ]),
    );
    const serviceByLabel = new Map<string, string>(
      Object.entries(serviceInterestLabels).map(([key, label]) => [
        label.toLowerCase(),
        key,
      ]),
    );

    // canton: translate the German label to the 2-letter code. Leave a value
    // that is ALREADY a valid code (idempotent re-run / agent path) untouched.
    if (typeof out.canton === 'string') {
      const raw = out.canton.trim();
      if (!(raw in cantonLabels)) {
        const code = cantonByLabel.get(raw.toLowerCase());
        if (code) out.canton = code;
      }
    }

    // serviceInterest: strip a trailing "(…)" qualifier (e.g. the form's
    // "Angehoerigenpflege (bezahlt)") then translate label → enum key. Leave an
    // already-canonical key untouched.
    if (typeof out.serviceInterest === 'string') {
      const raw = out.serviceInterest.trim();
      if (!(raw in serviceInterestLabels)) {
        const stripped = raw.replace(/\s*\(.*\)\s*$/, '').trim();
        const key =
          serviceByLabel.get(raw.toLowerCase()) ??
          serviceByLabel.get(stripped.toLowerCase());
        if (key) out.serviceInterest = key;
      }
    }

    return out;
  },

  enrichment: {
    providers: [],
    fallbackProviders: [],
    skipEnrichment: true,
  },

  scoring: {
    rules: [
      // Service interest must be set (routing field, hard requirement)
      {
        field: 'serviceInterest',
        operator: 'exists',
        value: true,
        points: 5,
        required: true,
        description: 'Service interest must be specified for routing',
      },
      // Service interest (commission-weighted scoring)
      {
        field: 'serviceInterest',
        operator: 'eq',
        value: 'angehoerigenpflege',
        points: 20,
        description:
          'Angehoerigenpflege: CHF 500 commission, highest-volume segment',
      },
      {
        field: 'serviceInterest',
        operator: 'eq',
        value: 'betreuung',
        points: 20,
        description: 'Betreuung: CHF 500 commission',
      },
      {
        field: 'serviceInterest',
        operator: 'eq',
        value: 'hauswirtschaft',
        points: 10,
        description: 'Hauswirtschaft: CHF 200 commission',
      },
      // Angehoerigenpflege path scoring. careHoursPerWeek is a graduated
      // engagement metric (more hours = better); a car is NOT a qualifier for
      // family carers (they care at home), so it earns no points.
      {
        field: 'careHoursPerWeek',
        operator: 'gte',
        value: 20,
        points: 20,
        description: 'Already caring >=20h/week (very high engagement)',
      },
      {
        field: 'careHoursPerWeek',
        operator: 'gte',
        value: 10,
        points: 15,
        description: 'Already caring >=10h/week (high engagement)',
      },
      {
        field: 'careHoursPerWeek',
        operator: 'gte',
        value: 5,
        points: 8,
        description: 'Already caring >=5h/week (moderate engagement)',
      },
      // Professional path scoring
      {
        field: 'qualifications',
        operator: 'contains',
        value: 'hf_pflege',
        points: 25,
        description: 'Dipl. Pflegefachperson HF (highest qualification)',
      },
      {
        field: 'qualifications',
        operator: 'contains',
        value: 'fh_pflege',
        points: 25,
        description: 'BSc Pflege FH (highest qualification)',
      },
      {
        field: 'qualifications',
        operator: 'contains',
        value: 'fage',
        points: 18,
        description: 'FaGe EFZ (strong mid-level qualification)',
      },
      {
        field: 'qualifications',
        operator: 'contains',
        value: 'fabe',
        points: 15,
        description: 'FaBe EFZ (care specialist)',
      },
      {
        field: 'experienceYears',
        operator: 'gte',
        value: 5,
        points: 20,
        description: '5+ years of experience',
      },
      {
        field: 'experienceYears',
        operator: 'gte',
        value: 2,
        points: 10,
        description: '2+ years of experience',
      },
      // Shared scoring
      {
        field: 'availability',
        operator: 'eq',
        value: 'full_time',
        points: 15,
        description: 'Available full-time',
      },
      {
        field: 'availability',
        operator: 'eq',
        value: 'part_time',
        points: 8,
        description: 'Available part-time',
      },
      {
        field: 'languages',
        operator: 'contains',
        value: 'de',
        points: 10,
        description: 'Speaks German (primary Swiss language)',
      },
      {
        field: 'languages',
        operator: 'contains',
        value: 'fr',
        points: 5,
        description: 'Speaks French (Romandie coverage)',
      },
      {
        field: 'languages',
        operator: 'contains',
        value: 'it',
        points: 3,
        description: 'Speaks Italian (Ticino/Italian speakers)',
      },
      // Instant-Form qualifying answers. The Spitex_Supply Lead Ad asks
      // `careRelationship` ("Ja, regelmaessig" / "Ja, gelegentlich" / "Nein,
      // noch nicht") and `canton`. These are the form's primary fit signals, so
      // they MUST carry scoring weight — otherwise a paid form lead scores only
      // on serviceInterest and the form qualifies on questions that score
      // nothing (the same failure mode as the live drivers form). The conversational
      // agent path collects careHoursPerWeek instead, which scores separately.
      {
        field: 'careRelationship',
        operator: 'eq',
        value: 'Ja, regelmaessig',
        points: 20,
        description:
          'Already caring for a relative regularly (strongest Angehoerigenpflege signal)',
      },
      {
        field: 'careRelationship',
        operator: 'eq',
        value: 'Ja, gelegentlich',
        points: 12,
        description: 'Caring for a relative occasionally (moderate signal)',
      },
      {
        // Region fit: any canton answer survives the form's disqualify branch
        // (which routes "Anderer Kanton" off the qualified path), so a present
        // canton on a qualified form lead means in-region coverage.
        field: 'canton',
        operator: 'exists',
        value: true,
        points: 8,
        description: 'In-region canton declared (operating-area fit)',
      },
    ],
    aiPromptTemplate:
      'Evaluate this caregiver for Spitex home care. Two segments: (1) Angehoerigenpflege: family members already caring for someone, no qualifications needed, CHF 500 commission. (2) Professional: nurses/carers with Swiss qualifications (HF, FaGe, AGS), CHF 500 (Betreuung) or CHF 200 (Hauswirtschaft) commission. Consider service interest, care engagement, qualifications, experience, languages, and availability. Score from 0-100.',
    thresholds: {
      qualified: 50,
      nurture: 25,
      discard: 15,
    },
  },

  outreach: {
    // Spitex SUPPLY = caregiver recruitment (job applications). WhatsApp-ONLY
    // per operator decision 2026-06-06: acquisition is inbound CTWA WhatsApp
    // and all follow-up stays on the same channel. Email/SMS retired for this
    // vertical (the 2026-06-04 PR #149 retirement removed WhatsApp instead —
    // inverted 2026-06-06 once the spitex_supply_application_followup UTILITY
    // template was approved on the IhrSpitex WABA, Meta id 27881312134824562).
    enabledChannels: ['whatsapp'],
    // Supply WhatsApp leaves from the "IhrSpitex Supply" ChatSuite session
    // (+41445055670, Meta WABA 1493171155695095). Replaces the global
    // CHATSUITE_SESSION_TEL, which wrongly sent every vertical from the drivers
    // WABA.
    chatsuiteSession: { tel: '+41445055670' },
    sequences: [SPITEX_SUPPLY_SEQUENCE],
    rateLimits: [{ channel: 'whatsapp', maxPerHour: 20, maxPerDay: 100 }],
    sendWindow: {
      days: [1, 2, 3, 4, 5, 6],
      startHour: 8,
      endHour: 19,
      timezone: 'Europe/Zurich',
    },
    // A/B experiment retired 2026-06-04 together with its only variant, the
    // WhatsApp-first `spitex-caregiver-whatsapp-first` sequence (see selenos
    // PR #149). With a single supply sequence remaining, resolveSequenceForLead
    // returns it directly and no allocation is needed.
  },

  compliance: {
    jurisdiction: 'ch-dsg-health',
    // The supply consent prompt ("...Sie bezueglich der Stelle kontaktieren?")
    // is an explicit UWG Art. 3(o) opt-in to ongoing commercial contact about
    // the job (operator policy 2026-04-30). With explicit `consentGiven=true`
    // on the agent path the capture layer records MARKETING-category channel
    // scope (whatsapp_marketing). The live follow-up sequence uses the UTILITY
    // template `spitex_supply_application_followup`, which only needs
    // implied-inbound service consent — the marketing scope is recorded as
    // headroom for any future MARKETING-category template, not as a gate for
    // the current sequence. Gated to explicit opt-in only (never implied-inbound).
    marketingConsent: true,
    consentRequirements: [
      'Explicit consent for processing personal data for job matching (FADP Art. 6(6)-(7))',
      'Proactive information disclosure at point of collection (FADP Art. 19)',
      'Purpose limitation: data used only for caregiver placement (FADP Art. 6(2))',
      // Prose mirror of `dataRetentionMonths` below, which is the single author.
      // This string renders to account users in the Playbook page
      // (apps/web/.../pipeline/architecture), so it must state the same number.
      // It said "active placement period + 1 year". The 12 comes from what
      // selenos.ai/de/datenschutz published to data subjects; see the comment on
      // `dataRetentionMonths` below for where that promise now lives.
      'Data retention limited to 12 months',
      'Right to data portability (FADP Art. 28) and erasure',
      'No cross-border transfers without adequate protection (FADP Art. 16)',
      'UWG Art. 3(o): opt-in required for email/WhatsApp/SMS outreach',
    ],
    dncEnabled: true,
    dpiaRequired: true,
    dpiaReference:
      'PENDING: DPIA required before processing health-adjacent data at scale (FADP Art. 22)',
    // SINGLE AUTHOR of the spitex retention period. Fixed at 12 months by
    // Antoine on 2026-08-15, replacing a 12/24/240-month contradiction.
    //
    // WHY 12 AND NOT A NUMBER CHOSEN FROM THE LAW. No law names one: FADP
    // Art. 6(4), GDPR Art. 5(1)(e) and POPIA s14(1) all say only "no longer
    // than necessary". The basis is a promise we published:
    // selenos.ai/de/datenschutz told people their non-converting enquiry is
    // kept "6 bis 12 Monate, danach Loeschung", shown to the data subject at
    // the moment of collection. Honouring it is both the defensible position
    // and the cheap one; 24 would have required changing a promise already
    // made to people whose data we already hold.
    //
    // WHAT THIS NUMBER ALSO OVERRIDES, AND WHAT IS STILL OPEN. That published
    // table carried a SECOND period, keyed on lead state: "Aktive
    // Vermittlungsdaten: Dauer der Vermittlung + 1 Jahr". That row, not the
    // 6-to-12 one, is what the prose above used to mirror. This file now
    // applies one period to every lead whatever its state, and
    // enforceRetention measures it from `created_at`, so a placement still
    // running at 12 months would be anonymised against that second promise.
    // The pages state a period per lead state and the code holds one value per
    // jurisdiction: the shape differs, not only the number. Reconciling them is
    // open, not settled here. See
    // docs/wip/active/WIP_making_the_published_legal_pages_true_2026_08_15.md
    // § Phase 2.
    //
    // WHERE TO READ THAT PROMISE NOW. Commit 8d9b27f4 (2026-08-15, "make the
    // published pages true") replaced the sentence with "Wie oben; eine feste
    // Frist legen wir derzeit fest", so a future reader will not find it on the
    // page. It was still being served at the time of writing, because that
    // commit had not deployed. Quote it from
    // `git show 8d9b27f4^:apps/web/app/[locale]/(marketing)/datenschutz/page.tsx`,
    // which is the durable source. The same commit also rewrote a "3 years
    // after your last interaction" sentence on the English privacy-policy page.
    // That one is the DRIVERS promise (za-popia) and is a separate open
    // question, not evidence for any number here.
    //
    // FOLLOW-UP, NOT DONE HERE, AND IT HAS AN ORDER. The pages now say a fixed
    // period is still being set, while this file has one. Publishing 12 there
    // must wait on `enforceRetention` actually being wired: it still has no
    // caller and no cron, so a firm period on the page would re-create the
    // unenforced promise that
    // docs/wip/active/WIP_making_the_published_legal_pages_true_2026_08_15.md
    // (R1, criterion 1) just removed. After the wiring it is a user-facing legal
    // copy change and Antoine's call.
    //
    // The 240 that used to sit in RETENTION_POLICIES was a medical-record
    // figure for a care provider holding a treatment record. Selenos is an
    // intermediary and treats nobody, so that duty never attached, and 20 years
    // against our own website saying 6 to 12 is not defensible under the
    // proportionality standard of FADP Art. 6(2).
    // Analysis: docs/wip/drafts/retention-decision-packet-2026-08-06.md § 1.
    // Decision: docs/wip/active/WIP_enforcing_data_retention_2026_08_03.md,
    // § "Décision révisée (2026-08-15)". Read that one, not the § Décision above
    // it: the first call that day was 24 months, taken on the grounds that 24 was
    // the only value any code read, before the published promise was weighed. It
    // is kept in the file, marked superseded, rather than rewritten.
    // Three mirrors must move with it: the prose above, the prose on the demand
    // config, and RETENTION_POLICIES['ch-dsg-health'] in @selenos/compliance.
    // Pinned by apps/web/lib/compliance/retention-period-consistency.test.ts.
    dataRetentionMonths: 12,
    dataProcessors: [
      {
        name: 'Vercel Inc.',
        purpose: 'Application hosting and edge compute',
        location: 'US (EU-adequate via Swiss-US DPF)',
      },
      {
        name: 'Supabase Inc.',
        purpose: 'Database and authentication',
        location: 'EU (Frankfurt)',
      },
      {
        name: 'Resend Inc.',
        purpose: 'Transactional email delivery',
        location: 'US (EU-adequate via Swiss-US DPF)',
      },
      { name: 'ChatSuite', purpose: 'WhatsApp messaging', location: 'EU' },
    ],
  },

  delivery: {
    adapter: 'supabase',
    format: 'json',
  },

  theme: {
    primaryColor: '#0D9488',
    secondaryColor: '#FFFFFF',
    accentColor: '#2563EB',
    heroStyle: 'light',
    personality: 'professional',
  },

  branding: {
    brandName: 'IhrSpitex',
    faviconIco: '/branding/spitex/favicon.ico',
    appleTouchIcon: '/branding/spitex/apple-touch-icon.png',
    logo: '/branding/spitex/logo.png',
    themeColor: '#0D9488',
  },

  email: {
    fromAddress: 'info@ihrspitex.ch',
    fromName: 'IhrSpitex',
    replyToAddress: 'spitex-supply@reply.selenos.ai', // inbound-captured (Resend Inbound; ihrspitex.ch mailbox unmonitored)
    brandHomepageUrl: 'https://ihrspitex.ch',
    applicationUrlTemplate: 'https://ihrspitex.ch/spitex-supply/{city}/apply',
    defaultCity: 'zurich',
    privacyPolicyUrl: 'https://ihrspitex.ch/datenschutz',
  },

  google: {
    conversionId: process.env.NEXT_PUBLIC_SPITEX_SUPPLY_GOOGLE_CONVERSION_ID,
    conversionLabel:
      process.env.NEXT_PUBLIC_SPITEX_SUPPLY_GOOGLE_CONVERSION_LABEL,
    qualificationConversionLabel:
      process.env.SPITEX_SUPPLY_GOOGLE_QUALIFICATION_LABEL,
    deliveryConversionLabel: process.env.SPITEX_SUPPLY_GOOGLE_DELIVERY_LABEL,
  },

  // Per-lead Slack alert for the manual hand-off to Spitex Frei. Delivery for
  // this client is manual (Antoine forwards each caregiver lead on WhatsApp),
  // so EVERY captured supply lead — regardless of score — is posted to a
  // dedicated channel in a copy-paste-ready format. Channel is config-driven:
  // SPITEX_FREI_LEADS_SLACK_CHANNEL overrides the `slackChannel` default.
  supplyLeadAlerts: {
    slackChannel: '#spitex-frei-leads',
    channelEnvVar: 'SPITEX_FREI_LEADS_SLACK_CHANNEL',
    // Quiz answers rendered into the forward-ready message, in ask order.
    fields: [
      {
        key: 'serviceInterest',
        label: 'Dienstleistung',
        valueLabels: serviceInterestLabels,
      },
      { key: 'canton', label: 'Kanton', valueLabels: cantonLabels },
      { key: 'city', label: 'Ortschaft' },
      { key: 'careRelationship', label: 'Pflegt' },
      { key: 'careActivities', label: 'Taetigkeiten' },
      { key: 'careHoursPerWeek', label: 'Stunden/Woche' },
      // Krankenkasse: REQUIRED for forwardability (Osman, 2026-06-06). The lead
      // only reaches mayComplete (alert-fire point) once this is collected, so
      // the card always carries it.
      { key: 'krankenkasse', label: 'Krankenkasse' },
      // Medizinische Ausbildung: captured, optional. `formatValue` returns null
      // for an absent value, so this line is gracefully omitted when not given.
      {
        key: 'medizinischeAusbildung',
        label: 'Medizinische Ausbildung',
        valueLabels: medizinischeAusbildungLabels,
      },
    ],
  },

  landingPage: {
    theme: {
      primaryColor: '#0D9488',
      secondaryColor: '#FFFFFF',
      fontFamily: 'Inter, sans-serif',
    },
    copyOverrides: {
      headline: 'Ihre Karriere in der Spitex-Pflege',
      subheadline: 'Flexible Einsaetze. Faire Bezahlung. Sinnvolle Arbeit.',
      ctaButton: 'Jetzt bewerben',
      formTitle: 'Erzaehlen Sie uns von Ihrer Erfahrung',
    },
    trustSignals: [
      { type: 'badge', content: 'nDSG-konform / Datenschutz garantiert' },
      { type: 'stat', content: 'Spitex-Organisationen in der ganzen Schweiz' },
      {
        type: 'testimonial',
        content:
          'Innerhalb einer Woche hatte ich drei passende Einsatzangebote.',
        attribution: 'Sandra K., dipl. Pflegefachfrau HF',
      },
    ],
    whatsappCtaUrl: process.env.NEXT_PUBLIC_SPITEX_SUPPLY_WHATSAPP_NUMBER
      ? `https://wa.me/${process.env.NEXT_PUBLIC_SPITEX_SUPPLY_WHATSAPP_NUMBER}?text=Guten%20Tag%2C%20ich%20interessiere%20mich%20fuer%20eine%20Stelle%20als%20Pflegefachperson.`
      : undefined,
  },
};

/**
 * Default vertical configuration for Spitex demand-side (patient/family intake).
 *
 * Targets Swiss market, DSG+health compliant, sourced via Meta Ads and referrals.
 * Outreach through email and phone.
 */
export const spitexDemandConfig: VerticalConfig = {
  id: 'a1000000-0000-0000-0000-000000000004',
  name: 'Spitex Patienten',
  slug: 'spitex-demand',
  adCampaignPatterns: ['spitex_demand', 'spitex-demand'],
  country: 'CH',
  language: 'de',
  capture: {
    // Deviation (ADR 0002): patient intake runs on the ihrspitex.ch web
    // widget agent; vertical descoped/dormant since 2026-06-01.
    primitive: 'widget_agent',
    deviationReason:
      'Patient intake runs on the ihrspitex.ch website widget agent; vertical descoped to backlog 2026-06-01.',
  },

  customFieldsSchema: SpitexDemandFieldsSchema,
  conversationConfig: spitexDemandConversationConfig,

  enrichment: {
    providers: [],
    fallbackProviders: [],
    skipEnrichment: true,
  },

  scoring: {
    rules: [
      // Service type must be set (hard requirement for routing)
      {
        field: 'serviceType',
        operator: 'exists',
        value: true,
        points: 5,
        required: true,
        description: 'Service type must be specified for care matching',
      },
      // Service type scoring (commission-weighted)
      {
        field: 'serviceType',
        operator: 'eq',
        value: 'behandlungspflege',
        points: 25,
        description:
          'Behandlungspflege (Infusionen/Spritzen): highest KVG rate CHF 63/h, cross-sell funnel entry point',
      },
      {
        field: 'serviceType',
        operator: 'eq',
        value: 'angehoerigenpflege',
        points: 25,
        description:
          'Angehoerigenpflege: CHF 500 commission, high LTV, USP service',
      },
      {
        field: 'serviceType',
        operator: 'eq',
        value: 'betreuung',
        points: 20,
        description:
          'Betreuung: CHF 500 commission, KVG-covered, standard demand',
      },
      {
        field: 'serviceType',
        operator: 'eq',
        value: 'hauswirtschaft',
        points: 10,
        description:
          'Hauswirtschaft: CHF 200 commission, self-pay, lower value',
      },
      // Family already caring (Angehoerigenpflege indicator)
      {
        field: 'familyAlreadyCaring',
        operator: 'eq',
        value: true,
        points: 15,
        description:
          'Family already providing care: strong Angehoerigenpflege candidate',
      },
      // Urgency scoring
      {
        field: 'urgency',
        operator: 'eq',
        value: 'sofort',
        points: 25,
        description: 'Immediate care need: highest priority',
      },
      {
        field: 'urgency',
        operator: 'eq',
        value: 'innerhalb_1_woche',
        points: 18,
        description: 'Care needed within 1 week',
      },
      {
        field: 'urgency',
        operator: 'eq',
        value: 'innerhalb_1_monat',
        points: 10,
        description: 'Care needed within 1 month',
      },
      {
        field: 'urgency',
        operator: 'eq',
        value: 'spaeter',
        points: 3,
        description: 'Care needed later: low urgency, nurture candidate',
      },
      // Coverage area scoring (primary market bonus)
      {
        field: 'canton',
        operator: 'eq',
        value: 'ZH',
        points: 10,
        description:
          'Zurich: primary launch market, highest operational readiness',
      },
      // Engagement signals
      {
        field: 'careDescription',
        operator: 'exists',
        value: true,
        points: 5,
        description: 'Provided care description: engaged, high-intent lead',
      },
      {
        field: 'email',
        operator: 'exists',
        value: true,
        points: 5,
        description: 'Provided email: reachable via multiple channels',
      },
    ],
    aiPromptTemplate:
      'Evaluate this home care request. Four service types: Behandlungspflege (injections/infusions at home, CHF 63/h KVG rate, cross-sell funnel entry), Angehoerigenpflege (family member employment, CHF 500 commission), Betreuung (professional care, CHF 500 commission), Hauswirtschaft (household help, CHF 200 commission). Behandlungspflege leads are highest value because they trigger cross-sell into additional services. Consider service type, urgency, family caregiving situation, and canton. Score from 0-100.',
    thresholds: {
      qualified: 50,
      nurture: 25,
      discard: 10,
    },
  },

  outreach: {
    // Spitex DEMAND = patient/family inbound requests for home care services.
    // Per operator policy 2026-04-30: lead consent applies to any channel for
    // the purpose stated in the consent prompt ("bezueglich Spitex-Pflege").
    //
    // CAVEAT for future contributors: patient identifiers + service-of-interest
    // imply a health context, which makes data flowing through these channels
    // "besonders schuetzenswerte Personendaten" under nDSG Art. 5(c). WhatsApp
    // and SMS messages traverse cross-border infrastructure (Meta US, MNO
    // gateways), which engages nDSG Art. 16 (transfer to a country without
    // adequate protection). Per the consent text, "Daten gemaess nDSG
    // verarbeitet" — but if message bodies start carrying actual health
    // details (diagnoses, medication, treatment plans), the consent text and
    // sequence templates need to be revisited with legal review. As long as
    // outbound messages stay generic ("a coordinator will call you"), the
    // current setup is defensible.
    enabledChannels: ['email', 'phone', 'whatsapp', 'sms'],
    // Demand WhatsApp leaves from the "IhrSpitex Demand" ChatSuite session
    // (+41445052527, Meta WABA 1493171155695095, where spitex_demand_*
    // templates live). Routing is correct; the number itself is being
    // re-verified (name_status DECLINED as of 2026-06-04), so sends fail-fast
    // at Meta until it is approved.
    chatsuiteSession: { tel: '+41445052527' },
    sequences: [SPITEX_DEMAND_SEQUENCE, SPITEX_DEMAND_NURTURE_SEQUENCE],
    rateLimits: [
      { channel: 'email', maxPerHour: 30, maxPerDay: 150 },
      { channel: 'phone', maxPerHour: 10, maxPerDay: 50 },
      { channel: 'whatsapp', maxPerHour: 20, maxPerDay: 100 },
      { channel: 'sms', maxPerHour: 20, maxPerDay: 80 },
    ],
    sendWindow: {
      days: [1, 2, 3, 4, 5, 6],
      startHour: 8,
      endHour: 19,
      timezone: 'Europe/Zurich',
    },
  },

  compliance: {
    jurisdiction: 'ch-dsg-health',
    consentRequirements: [
      'Explicit consent for processing health-related personal data (FADP Art. 6(6)-(7))',
      'Proactive information disclosure at point of collection (FADP Art. 19)',
      'Purpose limitation: data used only for care matching (FADP Art. 6(2))',
      'Data minimization: only collect what is necessary for placement',
      // Prose mirror of `dataRetentionMonths` below, which is the single author.
      // This string renders to account users in the Playbook page
      // (apps/web/.../pipeline/architecture), so it must state the same number.
      // It said "active care period + 1 year". The 12 comes from what
      // selenos.ai/de/datenschutz published to data subjects; see the comment on
      // `dataRetentionMonths` below for where that promise now lives.
      'Data retention limited to 12 months',
      'Right to data portability (FADP Art. 28) and erasure',
      'No cross-border transfers without adequate protection (FADP Art. 16)',
      'DPIA required for large-scale health data processing (FADP Art. 22)',
      'UWG Art. 3(o): opt-in required for email/phone outreach',
    ],
    dncEnabled: true,
    dpiaRequired: true,
    dpiaReference:
      'PENDING: DPIA required before processing health data at scale (FADP Art. 22)',
    // SINGLE AUTHOR of the spitex retention period, demand side. Same 12-month
    // decision and same caveats as the supply config above; see that comment.
    // Pinned by apps/web/lib/compliance/retention-period-consistency.test.ts.
    dataRetentionMonths: 12,
    dataProcessors: [
      {
        name: 'Vercel Inc.',
        purpose: 'Application hosting and edge compute',
        location: 'US (EU-adequate via Swiss-US DPF)',
      },
      {
        name: 'Supabase Inc.',
        purpose: 'Database and authentication',
        location: 'EU (Frankfurt)',
      },
      {
        name: 'Resend Inc.',
        purpose: 'Transactional email delivery',
        location: 'US (EU-adequate via Swiss-US DPF)',
      },
      { name: 'ChatSuite', purpose: 'WhatsApp messaging', location: 'EU' },
    ],
  },

  delivery: {
    adapter: 'supabase',
    format: 'json',
  },

  theme: {
    primaryColor: '#0D9488',
    secondaryColor: '#FFFFFF',
    accentColor: '#2563EB',
    heroStyle: 'light',
    personality: 'professional',
  },

  branding: {
    brandName: 'IhrSpitex',
    faviconIco: '/branding/spitex/favicon.ico',
    appleTouchIcon: '/branding/spitex/apple-touch-icon.png',
    logo: '/branding/spitex/logo.png',
    themeColor: '#0D9488',
  },

  email: {
    fromAddress: 'info@ihrspitex.ch',
    fromName: 'IhrSpitex',
    replyToAddress: 'spitex-demand@reply.selenos.ai', // inbound-captured (Resend Inbound; ihrspitex.ch mailbox unmonitored)
    brandHomepageUrl: 'https://ihrspitex.ch',
    applicationUrlTemplate: 'https://ihrspitex.ch/spitex-demand/{city}/apply',
    defaultCity: 'zurich',
    privacyPolicyUrl: 'https://ihrspitex.ch/datenschutz',
  },

  google: {
    conversionId: process.env.NEXT_PUBLIC_SPITEX_DEMAND_GOOGLE_CONVERSION_ID,
    conversionLabel:
      process.env.NEXT_PUBLIC_SPITEX_DEMAND_GOOGLE_CONVERSION_LABEL,
    qualificationConversionLabel:
      process.env.SPITEX_DEMAND_GOOGLE_QUALIFICATION_LABEL,
    deliveryConversionLabel: process.env.SPITEX_DEMAND_GOOGLE_DELIVERY_LABEL,
  },

  landingPage: {
    theme: {
      primaryColor: '#0D9488',
      secondaryColor: '#FFFFFF',
      fontFamily: 'Inter, sans-serif',
    },
    copyOverrides: {
      headline:
        'Endlich verstehen, was Spitex kostet und was die Krankenkasse zahlt.',
      subheadline:
        'Kostenlose Beratung, gepruefte Anbieter, persoenliche Begleitung. IhrSpitex hilft Ihnen, die richtige Pflege zu Hause zu finden.',
      ctaButton: 'Jetzt kostenlos anfragen',
      formTitle: 'Ihre unverbindliche Anfrage',
    },
    trustSignals: [
      { type: 'badge', content: 'Kostenlos und unverbindlich' },
      { type: 'badge', content: 'Nur gepruefte Spitex-Organisationen' },
      {
        type: 'badge',
        content: 'Datenschutz nach Schweizer Recht (nDSG)',
      },
    ],
    whatsappCtaUrl: process.env.NEXT_PUBLIC_SPITEX_DEMAND_WHATSAPP_NUMBER
      ? `https://wa.me/${process.env.NEXT_PUBLIC_SPITEX_DEMAND_WHATSAPP_NUMBER}?text=Guten%20Tag%2C%20ich%20benoetige%20Informationen%20ueber%20Spitex-Pflege.`
      : undefined,
  },
};
